import { RangeLevels } from "../types/gauge";

/**
 * Whether a band series has all nine band values, each a real number. The
 * cache mapper fails a row without them, so the band math never runs on a
 * partial set.
 */
export const hasAllBandValues = (rangesLevelSeries: unknown): rangesLevelSeries is number[] =>
  Array.isArray(rangesLevelSeries) &&
  rangesLevelSeries.length === 9 &&
  rangesLevelSeries.every((v) => typeof v === "number" && Number.isFinite(v));

/**
 * Converts the ranges_level_series array into a structured RangeLevels object
 * with named properties for better code readability and maintainability.
 *
 * @param rangesLevelSeries Array of 9 values representing percentile levels
 * @returns RangeLevels object with named percentile properties
 */
export function createRangeLevels(rangesLevelSeries: number[]): RangeLevels {
  return {
    pct0: rangesLevelSeries[0], // 0th percentile (minimum)
    pct2p5: rangesLevelSeries[1], // 2.5th percentile
    pct16: rangesLevelSeries[2], // 16th percentile
    pct45: rangesLevelSeries[3], // 45th percentile (left edge of center)
    pct50: rangesLevelSeries[4], // 50th percentile (median)
    pct55: rangesLevelSeries[5], // 55th percentile (right edge of center)
    pct84: rangesLevelSeries[6], // 84th percentile
    pct97p5: rangesLevelSeries[7], // 97.5th percentile
    pct100: rangesLevelSeries[8], // 100th percentile (maximum)
  };
}

/**
 * Helper function to get center range width for calculations
 */
export function getCenterRangeWidth(rangeLevels: RangeLevels): number {
  return rangeLevels.pct55 - rangeLevels.pct45;
}

/**
 * Helper function to check if center segment has collapsed to a point
 */
export function isCenterCollapsed(rangeLevels: RangeLevels): boolean {
  return getCenterRangeWidth(rangeLevels) === 0;
}

/**
 * The gauge's band boundaries, from the outermost low edge (left4) through the
 * edges of the average band (left1, right1) to the outermost high edge (right4).
 */
export interface BandBoundaries {
  left4: number;
  left3: number;
  left2: number;
  left1: number;
  right1: number;
  right2: number;
  right3: number;
  right4: number;
}

/**
 * Band boundaries around the 10-year average. The gauge needle and the explainer
 * text both read these, so they always agree on which band a value is in.
 *
 * Each pair sits symmetrically around the average, half as far out as the
 * matching percentile spread. The average band is exactly as wide as the
 * 45th-55th percentile spread, with no minimum width. For a series reported to
 * one decimal place, 10% or more of its history can share one value, so the 45th
 * and 55th percentiles coincide and the average band has no width. The outer
 * bands keep their widths, so a value away from the average still lands in the
 * band its distance calls for. An absolute minimum width would not scale: 0.0001
 * is wider than the entire "somewhat" band of a series whose values are that small.
 */
export function getBandBoundaries(rangeLevels: RangeLevels, tenYearAverage: number): BandBoundaries {
  const halfSpread = (low: number, high: number) => (high - low) / 2;
  const outer = halfSpread(rangeLevels.pct0, rangeLevels.pct100);
  const third = halfSpread(rangeLevels.pct2p5, rangeLevels.pct97p5);
  const second = halfSpread(rangeLevels.pct16, rangeLevels.pct84);
  const center = getCenterRangeWidth(rangeLevels) / 2;

  return {
    left4: tenYearAverage - outer,
    left3: tenYearAverage - third,
    left2: tenYearAverage - second,
    left1: tenYearAverage - center,
    right1: tenYearAverage + center,
    right2: tenYearAverage + second,
    right3: tenYearAverage + third,
    right4: tenYearAverage + outer,
  };
}
